import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { contractObservationService } from "../services/contractObservationService";
import { contractObservationDisplayId } from "./ContractObservationCommonStore";
import { ContractObservation } from "../../../api/graphql";
import { authorComposerDisplayId } from "../../authorComposer/store/AuthorComposerCommonStore";
import { publisherDisplayId } from "../../publisher/store/PublisherCommonStore";
import { authorComposerService } from "../../authorComposer/services/authorComposerService";
import { publisherService } from "../../publisher/services/publisherService";
import { InitializeParams } from "../../../store/types";

export class ContractObservationViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = contractObservationService;
  contractObservationInstance: ContractObservation | null = null;
  cardState: Record<string, boolean> = { filters: true };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  titleValue: string = "";
  observationValue: string = "";
  requireAllValue: boolean = false;
  authorsService = authorComposerService;
  authorsSearchValue: string = "";
  authorsIdValue: string = "";
  authorsSelectedValues: { displayId: string; id: string }[] = [];
  publishersService = publisherService;
  publishersSearchValue: string = "";
  publishersIdValue: string = "";
  publishersSelectedValues: { displayId: string; id: string }[] = [];

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadContractObservationById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.contractObservationOne({ id });
    this.contractObservationInstance = result.contractObservationOne;
    if (this.contractObservationInstance) {
      this.loadValues(this.contractObservationInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.contractObservationInstance) {
      return contractObservationDisplayId(
        this.contractObservationInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.ContractObservationSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "authors") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.authorComposerOne({
          id: params.returnData.resultId,
        });
        const authorComposer = result.authorComposerOne;
        this.authorsSelectedValues.push(authorComposer);
      } else if (params.callerData?.fieldName === "publishers") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.publisherOne({
          id: params.returnData.resultId,
        });
        const publisher = result.publisherOne;
        this.publishersSelectedValues.push(publisher);
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get authorsValues() {
    return this.authorsSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: authorComposerDisplayId(x, this.langStore),
      };
    });
  }

  get publishersValues() {
    return this.publishersSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: publisherDisplayId(x, this.langStore),
      };
    });
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.contractObservationInstance = null;
    this.titleValue = "";
    this.observationValue = "";
    this.requireAllValue = false;
    this.authorsSearchValue = "";
    this.authorsIdValue = "";
    this.authorsSelectedValues = [];
    this.publishersSearchValue = "";
    this.publishersIdValue = "";
    this.publishersSelectedValues = [];
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelContractObservation() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions(
      "contractObservation_update"
    );
  }

  editContractObservation() {
    const id = this.contractObservationInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/contractObservation/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printContractObservation(handlePrint: () => void): void {
    handlePrint();
  }

  *closeContractObservation(): any {
    yield this.close();
  }

  loadValues(instance: ContractObservation) {
    this.idValue = instance.id;
    this.titleValue = instance.title;
    this.observationValue = instance.observation;
    this.requireAllValue = instance.requireAll || false;
    this.authorsSelectedValues = instance.authors ? instance.authors : [];
    this.publishersSelectedValues = instance.publishers
      ? instance.publishers
      : [];
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadContractObservationById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
